import React, { useEffect, useState, useRef } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import DataTableManual from "components/Common/DataTableManual/DataTableManual";
import { loadSiteAssetsStart, setAssetId, setSiteAssetViewUid } from "services/redux/actions/serviceDesk";
import { getSiteAssets, getSiteAssetFields } from 'services/redux/selectors/serviceDesk/assets';
import { Button } from "react-bootstrap";

import Loading from "components/Loading";
import Icon from "components/Icons/Icon";

import AddSiteAsset from './AddSiteAsset/AddSiteAsset';

import "../AssetEditor.scss"; 

function AssetDetails({ 
  pagination, 
  loadingSiteAssets, 
  sudoSiteId, 
  localAssetId, 
  navName,
  setLocalAssetId, 
  setNavSiteViewName, 
  setSiteAssetView, 
  setViewAssetDetails,
  mctClusterId,
  mobileAppId,
  forceReload,
  setForceReload,
}) {
  const [rowData, setRowData] = useState([]);
  const [assetFields, setAssetFields] = useState([]);
  const [renderOnLoad, setRenderOnLoad] = useState(false);
  const [showAddSiteAssetModal, setShowAddSiteAssetModal] = useState(false);
  const [filtered, setFiltered] = React.useState([]);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryId = queryParams.get('category-id') ?? "";

  const handleModalShow = () => {
    setShowAddSiteAssetModal(true);
  }
  
  const handleModalClose = () => {
    setShowAddSiteAssetModal(false);
  }

  useEffect(() => {
    setSiteAssetView(false);
    setViewAssetDetails(true);
    setNavSiteViewName(false);
    if (!localAssetId && categoryId) {
      setLocalAssetId(categoryId);
    }
  }, [setSiteAssetView, setViewAssetDetails, setNavSiteViewName, localAssetId, setLocalAssetId, categoryId])

  const siteAssetsData = useSelector(getSiteAssets);
  const siteAssetFields = useSelector(getSiteAssetFields);
  const dispatch = useDispatch();

  const storeAssetId = useSelector(state => state.serviceDesk.assets.site.data.assetId);
  const overrideLocal = useSelector(state => state.serviceDesk.assets.site.data.overrideLocal);

  const loadingMsg = loadingSiteAssets ? <Loading center /> : "No data available...";
  
  const missingData = () => {
    return <span>N/A</span>
  }

  const stringToSnakeCase = (str) => {
    return str
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .replace(/[\s\-]+/g, '_')
      .toLowerCase();
  }

  // delays rendering of table results until fresh data is loaded
  const prevLoadingRef = useRef(loadingSiteAssets);
  const smoothRenderOnLoad = () => {
    if (prevLoadingRef.current === true && loadingSiteAssets === false) {
      setTimeout(() => {
        setRenderOnLoad(false);
      }, [50])
    }
    prevLoadingRef.current = loadingSiteAssets;
  }

  const loadSites = (reload) => {
    // check if empty or category id change
    const assetsEmpty = Object.values(siteAssetsData).length <= 0;
    const assetIdChanged = localAssetId !== storeAssetId;

    // core load functionality
    const loadData = () => {
      setRenderOnLoad(true);
      dispatch(loadSiteAssetsStart({
        assetId: overrideLocal ? storeAssetId : localAssetId,
        sudoSiteId,
      }));
      overrideLocal && setLocalAssetId(storeAssetId);
      dispatch(setAssetId({
        assetId: localAssetId,
        overrideLocal: false,
      }));
    }
    
    // logic
    if (reload) {
      loadData();
      setRenderOnLoad(true);
      forceReload && setForceReload(false);
    } else if ((localAssetId || storeAssetId && sudoSiteId) && (assetsEmpty || assetIdChanged)) {
      loadData();
      setRenderOnLoad(true);
    }
  }

  // loading sites hook
  useEffect(() => {
    smoothRenderOnLoad();
    loadSites(forceReload);
  }, [
      localAssetId, 
      storeAssetId, 
      sudoSiteId, 
      siteAssetsData, 
      dispatch, 
      overrideLocal, 
      loadingSiteAssets, 
      setRenderOnLoad,
      forceReload,
    ]);

  useEffect(() => {
    if (siteAssetFields.length > 0) {
      setAssetFields(siteAssetFields.map(item => item.field_name))
      const mappedAssets = Object.values(siteAssetsData)?.[0].map(data => data);
      setRowData(mappedAssets)
    }
  }, [siteAssetsData, setAssetFields, setRowData, siteAssetFields])

  const columns = assetFields.map(item => {
    const string = item.toString();

    const header = (string) => {
      if (!string) return "";
      
      return string
        .split(" ")
        .map(word => word.charAt(0)?.toUpperCase() + word.slice(1)?.toLowerCase()) 
        .join(" ");
    };

    if (string === "Photo") {
      return {
        key: "PHOTO",
        Header: "Image",
        id: "photo",
        type: "image",
        filterable: false,
        width: 150,
        accessor: data => {
          const url = data?.asset_values?.Photo?.file_url;
          return (
            url ?
              <img className="company-asset-image" src={url} alt="site-asset" />
            :
              <span className='ml-1'>{missingData()}</span>
          )
        },
      }
    }

    return {
      key: `${string.toUpperCase()}`,
      Header: `${header(string)}`,
      id: stringToSnakeCase(string),
      filterable: true,
      minWidth: 200,
      Cell: (props) => {
        // const index = header(string);
        const index = string;
        const value = props.original?.asset_values?.[index]?.value ?? null;
        const type = props?.original?.asset_values?.[index]?.type;
        // optional colour value for dropdown fields to determine circle icon colour
        const colour = props?.original?.asset_values?.[index]?.colour ?? 'lightgray';

        return (
          <div className='data-table-cell hide-scrollbar'>
            { index === 'Name' ?
              <span
                className='site-view-link'
              >
                <Icon className="fas fa-external-link-square-alt mr-2"></Icon>
                <span
                  onClick={() => {
                    setNavSiteViewName(value !== null ? value : (navName !== null ? navName : "Site Asset"))
                    setSiteAssetView(true);
                    const uid = props.original?.asset_details?.asset_uid;
                    uid && dispatch(setSiteAssetViewUid(uid));
                  }}
                >
                  {value}
                </span>
              </span>
            :
              // appends circle icon before text value if colour value is present
              type === "dropdown" && colour && value ? 
                <span className='d-flex align-items-center ml-1'>
                  <Icon style={{ color: colour ?? 'lightgray' }} className='fas fa-circle fa-xs mr-2' />
                  <span>{value}</span>
                </span>
            :
              value ?? missingData()
            }
          </div>
        )
      },
    }
  })

  return (
    <div>
      <div className='d-flex justify-content-end align-items-center mb-2'>
        <Button
          data-intercom-target={"add-site-asset-button"}
          id="add-asset-button"
          className="table-buttons"
          onClick={() => {
            handleModalShow()
          }}
        >
          <Icon className="fas fa-plus" title="Add Asset" />
        </Button>
      </div>

      {localAssetId && sudoSiteId &&
        <DataTableManual
          className={`${renderOnLoad ? "d-none" : ""} asset-details-table`}
          columns={columns}
          data={rowData}
          defaultPageSize={20}
          noDataText={loadingMsg}
          loadDataAction={loadSiteAssetsStart}
          loadDataParams={{ 
            assetId: localAssetId,
            sudoSiteId: sudoSiteId,
          }}
          filterable={true}
          filtered={filtered}
          onFilteredChange={newFiltered => setFiltered(newFiltered)}
          pages={pagination && pagination.pageCount}
          getTdProps={() => ({
            className: 'hide-scrollbar',
          })}
        />
      }  

      <AddSiteAsset
        assetFields={assetFields}
        siteAssetFields={siteAssetFields}
        handleClose={handleModalClose} 
        handleShow={handleModalShow}
        navName={navName}
        showModal={showAddSiteAssetModal}
        sudoSiteId={sudoSiteId}
        localAssetId={localAssetId}
        mctClusterId={mctClusterId}
        mobileAppId={mobileAppId}
        setForceReload={setForceReload}
      />

    </div>
  )
}

const mapStateToProps = (state, props) => {
  return {
    pagination: state.serviceDesk.assets.site.data.pagination,
    siteAssetData: state.serviceDesk.assets.site.data,
    loadingSiteAssets: state.serviceDesk.assets.site.loadingSiteAssets,
  };
};

export default connect(mapStateToProps)(AssetDetails)